<template>
  <div class="magic-session stack tight">
    <UiPanel tone="social">
      <div class="header">
        <div>
          <h3 class="title">{{ ICONS.skills.magic }} Magic</h3>
          <div class="subtitle text-muted">Casting state and spellbook.</div>
        </div>
        <UiChip v-if="castingActionResolved" :variant="castingActionResolved.isPaused ? 'paused' : 'active'">
          {{ castingActionResolved.isPaused ? "Paused" : "Active" }}
        </UiChip>
      </div>
    </UiPanel>

    <UiPanel tone="social" v-if="castingActionResolved">
      <UiSectionHeader>
        <template #title>Casting</template>
        <template #actions>
          <UiChip variant="muted" class="stat-mono num">
            {{ castingActionResolved.spellName }}
          </UiChip>
        </template>
      </UiSectionHeader>

      <div class="casting-body">
        <div class="casting-meta">
          <div>
            <div class="kv-label">Remaining</div>
            <div class="kv-value stat-mono num">{{ remainingLabel }}</div>
          </div>
          <div>
            <div class="kv-label">Status</div>
            <div class="kv-value stat-mono num">{{ castingActionResolved.isPaused ? "Paused" : "Casting" }}</div>
          </div>
        </div>

        <UiProgressBar :value="progressPct" tone="social" />

        <div class="row wrap" style="justify-content: flex-end;">
          <UiButton
            v-if="castingActionResolved.isPaused"
            size="sm"
            variant="secondary"
            @click="emit('resume')"
          >
            Resume
          </UiButton>
          <UiButton size="sm" variant="danger" @click="emit('cancel')">
            Cancel
          </UiButton>
        </div>
      </div>
    </UiPanel>

    <UiCallout v-else variant="muted">
      No active casting action.
    </UiCallout>

    <UiPanel tone="social">
      <UiSectionHeader>
        <template #title>Spellbook</template>
        <template #actions>
          <UiChip v-if="spellbookResolved" variant="muted" class="stat-mono num">
            {{ formatNumber(spellbookResolved.readied.length) }}/{{ formatNumber(spellbookResolved.readyLimit) }} readied
          </UiChip>
        </template>
      </UiSectionHeader>

      <UiCallout v-if="!spellbookResolved" variant="info">
        Spellbook loading is still being wired into the area snapshot.
      </UiCallout>

      <template v-else>
        <div v-if="spellbookResolved.readied.length === 0" class="text-muted">No spells readied.</div>
        <div v-else class="spell-grid">
          <div v-for="s in spellbookResolved.readied" :key="s.id" class="spell-card">
            <div class="spell-card__name">{{ s.name }}</div>
            <div class="text-muted spell-card__meta">Readied</div>
            <div class="spell-card__actions">
              <UiButton
                size="sm"
                variant="secondary"
                :disabled="!!castingActionResolved"
                @click="emit('cast', s.id)"
              >
                Cast
              </UiButton>
              <UiButton size="sm" variant="ghost" @click="emit('unready', s.id)">Unready</UiButton>
            </div>
          </div>
        </div>

        <UiSectionHeader style="margin-top: 0.9rem;">
          <template #title>Learned</template>
          <template #actions>
            <UiChip variant="muted" class="stat-mono num">{{ formatNumber(spellbookResolved.learned.length) }}</UiChip>
            <UiChip v-if="readySlotsRemaining !== null" variant="muted" class="stat-mono num">
              {{ formatNumber(readySlotsRemaining) }} slots
            </UiChip>
          </template>
        </UiSectionHeader>

        <div v-if="spellbookResolved.learned.length === 0" class="text-muted">No spells learned yet.</div>
        <div v-else class="spell-grid learned">
          <div v-for="s in spellbookResolved.learned" :key="s.id" class="spell-card" :class="{ disabled: isReadied(s.id) }">
            <div class="spell-card__name">{{ s.name }}</div>
            <div class="text-muted spell-card__meta">{{ isReadied(s.id) ? "Readied" : "Ready to slot" }}</div>
            <div class="spell-card__actions">
              <UiButton
                size="sm"
                variant="secondary"
                :disabled="isReadied(s.id) || readySlotsRemaining === 0"
                @click="emit('ready', s.id)"
              >
                Ready
              </UiButton>
            </div>
          </div>
        </div>
      </template>
    </UiPanel>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import type { CastingAction, Spell } from "@idle-rpg/shared";
import { ICONS } from "../../../lib/icons";
import { formatMs, formatNumber } from "../../../lib/format";
import UiButton from "../../../components/ui/UiButton.vue";
import UiCallout from "../../../components/ui/UiCallout.vue";
import UiChip from "../../../components/ui/UiChip.vue";
import UiPanel from "../../../components/ui/UiPanel.vue";
import UiProgressBar from "../../../components/ui/UiProgressBar.vue";
import UiSectionHeader from "../../../components/ui/UiSectionHeader.vue";

const props = defineProps<{
  castingAction: CastingAction | null;
  spellbook: { learned: Spell[]; readied: Spell[]; readyLimit: number } | null;
}>();

const emit = defineEmits<{
  resume: [];
  cancel: [];
  cast: [spellId: string];
  ready: [spellId: string];
  unready: [spellId: string];
}>();

const nowMs = ref(Date.now());
let timer: number | null = null;

onMounted(() => {
  timer = window.setInterval(() => {
    nowMs.value = Date.now();
  }, 250);
});

onBeforeUnmount(() => {
  if (timer) window.clearInterval(timer);
  timer = null;
});

const castingActionResolved = computed(() => props.castingAction ?? null);
const spellbookResolved = computed(() => props.spellbook ?? null);
const readySlotsRemaining = computed(() => {
  if (!spellbookResolved.value) return null;
  return Math.max(0, spellbookResolved.value.readyLimit - spellbookResolved.value.readied.length);
});
const readiedIds = computed(() => new Set(spellbookResolved.value?.readied.map((s) => s.id) ?? []));

function isReadied(id: string) {
  return readiedIds.value.has(id);
}

const remainingLabel = computed(() => {
  const action = castingActionResolved.value;
  if (!action) return "—";
  if (action.isPaused) return "Paused";
  const completes = Date.parse(action.completesAt);
  if (!Number.isFinite(completes)) return "—";
  return formatMs(Math.max(0, completes - nowMs.value));
});

const progressPct = computed(() => {
  const action = castingActionResolved.value;
  if (!action) return 0;
  const started = Date.parse(action.startedAt);
  const completes = Date.parse(action.completesAt);
  const denom = Math.max(1, Number.isFinite(started) && Number.isFinite(completes) ? completes - started : 1);
  const elapsed = Math.max(0, Math.min(denom, nowMs.value - (Number.isFinite(started) ? started : nowMs.value)));
  return Math.max(0, Math.min(100, (elapsed / denom) * 100));
});
</script>

<style scoped>
.magic-session {
  min-width: 0;
}

.header {
  display: flex;
  justify-content: space-between;
  gap: var(--space-md);
  align-items: flex-start;
}

.title {
  margin: 0;
  font-family: var(--font-display);
  letter-spacing: 0.02em;
}

.subtitle {
  font-size: 0.9rem;
  margin-top: 0.15rem;
}

.casting-body {
  margin-top: 0.75rem;
  display: grid;
  gap: 0.75rem;
}

.casting-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.kv-label {
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--fg-1);
}

.kv-value {
  font-size: 1.05rem;
  font-weight: 800;
  margin-top: 0.25rem;
}

.spell-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.spell-grid.learned {
  margin-top: 0.4rem;
}

.spell-card {
  border: 1px solid var(--border-0);
  border-radius: var(--radius-1);
  background: color-mix(in srgb, var(--bg-2) 80%, transparent);
  padding: 0.65rem 0.75rem;
  min-width: 160px;
  display: grid;
  gap: 0.4rem;
}

.spell-card.disabled {
  opacity: 0.7;
}

.spell-card__name {
  font-weight: 800;
}

.spell-card__meta {
  font-size: 0.8rem;
}

.spell-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}
</style>
